import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import QueryString from "qs";
import { CheckCircle, Circle, XCircle } from "@phosphor-icons/react";
import { ReqMethods, ghl } from "../../../enums";
import { useAppServices } from "../../../hook/services";

const GENERIC_ERROR = "Something went wrong while integrating, please try later";

const STEPS = [
  { key: "app", label: "Verifying app" },
  { key: "authorize", label: "Authorizing with CRM" },
  { key: "account", label: "Fetching account details" },
  { key: "save", label: "Saving connection" },
];

const StepIcon = ({ state, size = 22 }) => {
  if (state === "done")
    return <CheckCircle size={size} weight="fill" color="#16a34a" />;
  if (state === "error")
    return <XCircle size={size} weight="fill" color="#dc2626" />;
  if (state === "active")
    return (
      <span
        className="spinner-border spinner-border-sm text-primary"
        role="status"
        style={{ width: size * 0.82, height: size * 0.82, borderWidth: size > 30 ? 4 : undefined }}
      />
    );
  return <Circle size={size} color="#cbd5e1" />;
};

function IntegrateApp() {
  const { appId } = useParams();
  const { search } = useLocation();
  const code = new URLSearchParams(search).get("code");
  const AppServices = useAppServices();

  // holds what each step produces so a retry can resume without re-using the one-time code
  const ctx = useRef({});
  const started = useRef(false);

  const [app, setApp] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");

  const verifyApp = async () => {
    if (!code)
      throw new Error(
        "The authorization code is missing from this link. Please start the installation again."
      );

    const { response } = await AppServices.apps.filter({
      query: `appId=${appId}`,
    });
    if (!response?.data)
      throw new Error(
        "We couldn't find this app. Please check the installation link."
      );

    ctx.current.app = response.data;
    setApp(response.data);
  };

  const authorize = async () => {
    const { app } = ctx.current;
    const { response } = await AppServices.services.ghl.call_service({
      payload: {
        method: ReqMethods.POST,
        path: ghl.APIs.oauth,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        payload: QueryString.stringify({
          client_id: app.clientId,
          client_secret: app.clientSecret,
          grant_type: "authorization_code",
          code,
          refresh_token: "",
        }),
      },
    });
    if (!response?.data?.access_token)
      throw new Error(
        "The CRM could not authorize this installation. The link may have expired, please install the app again."
      );

    ctx.current.creds = response.data;
  };

  const loadAccount = async () => {
    const { app, creds } = ctx.current;
    const isLocation = !!creds.locationId;
    const isAgency = !isLocation && app?.appType === "agency";
    if (!isLocation && !isAgency) return;

    const { response } = await AppServices.services.ghl.call_service({
      payload: {
        method: ReqMethods.GET,
        path: isLocation
          ? ghl.APIs.v2.location(creds.locationId)
          : ghl.APIs.v2.company(creds.companyId),
        key: creds.access_token,
        refresh_token: creds.refresh_token,
      },
    });
    if (!response?.data)
      throw new Error("We couldn't load your account details from the CRM.");

    if (isLocation) ctx.current.location = response.data.location;
    else ctx.current.company = response.data.company;
  };

  const save = async () => {
    const { app, creds, company, location } = ctx.current;
    const { response, error } = await AppServices.auth.integrate_app({
      payload: {
        companyName: company?.name,
        locationName: location?.name,
        userId: creds.userId,
        userType: creds.userType,
        locationId: creds.locationId,
        companyId: creds.companyId,
        access_token: creds.access_token,
        refresh_token: creds.refresh_token,
        company,
        location,
        appId,
        appRefId: app._id,
        appType: app?.appType,
        appName: app?.appName,
        whoCanInstall: app?.whoCanInstall,
      },
    });
    if (!response || response.success === false)
      throw new Error(response?.message || error?.message || GENERIC_ERROR);

    setAccountName(location?.name || company?.name || "");
  };

  const pipeline = [verifyApp, authorize, loadAccount, save];

  const run = async (from = 0) => {
    setStatus("loading");
    setError("");
    for (let i = from; i < pipeline.length; i++) {
      setStepIndex(i);
      try {
        await pipeline[i]();
      } catch (err) {
        setError(err?.message || GENERIC_ERROR);
        setStatus("error");
        return;
      }
    }
    setStepIndex(pipeline.length);
    setStatus("success");
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepState = (i) => {
    if (status === "success" || i < stepIndex) return "done";
    if (i === stepIndex) return status === "error" ? "error" : "active";
    return "pending";
  };

  const title = {
    loading: "Connecting your app",
    success: "App connected successfully",
    error: "Integration failed",
  }[status];

  const subtitle = {
    loading: "Please keep this tab open while we finish setting things up.",
    success: `${app?.appName || "Your app"} is now connected${
      accountName ? ` to ${accountName}` : ""
    }. You can close this tab and return to your account.`,
    error,
  }[status];

  return (
    <div
      className="d-flex align-items-center justify-content-center min-vh-100 p-3"
      style={{ background: "linear-gradient(135deg, #f5f7ff, #eef2f7)" }}
    >
      <div
        className="card border-0 shadow-lg w-100"
        style={{ maxWidth: 460, borderRadius: 16 }}
      >
        <div className="card-body p-4 p-md-5 text-center">
          {/* No app logo here — just the overall status, shown on its own. */}
          <div
            className="d-inline-flex align-items-center justify-content-center mb-4"
            style={{ width: 72, height: 72 }}
          >
            <StepIcon
              size={56}
              state={
                status === "success"
                  ? "done"
                  : status === "error"
                  ? "error"
                  : "active"
              }
            />
          </div>

          <h4 className="fw-bold mb-2">{title}</h4>
          <p
            className={`mb-4 ${status === "error" ? "text-danger" : "text-muted"}`}
            role="status"
            aria-live="polite"
            style={{ whiteSpace: "pre-line" }}
          >
            {subtitle}
          </p>

          <ul className="list-unstyled text-start mb-0 border rounded-3 p-3">
            {STEPS.map((step, i) => {
              const state = stepState(i);
              return (
                <li
                  key={step.key}
                  className="d-flex align-items-center gap-3 py-2"
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 22, height: 22 }}
                  >
                    <StepIcon state={state} />
                  </span>
                  <span
                    className={
                      state === "pending"
                        ? "text-muted"
                        : state === "error"
                        ? "text-danger fw-medium"
                        : "fw-medium"
                    }
                  >
                    {step.label}
                  </span>
                </li>
              );
            })}
          </ul>

          {status === "error" && (
            <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={() => run(stepIndex)}
              >
                Try again
              </button>
              <p className="text-muted small mt-3 mb-0">
                If this keeps happening, please install the app again or
                contact support.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default IntegrateApp;
